import classNames from "classnames";
import { decamelizeKeys } from "humps";
import AppContext from "PFApp/app_context";
import { useSession } from "PFApp/use_session";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import DataTable from "PFComponents/data_table/data_table";
import DropdownButton from "PFComponents/dropdown/dropdown_button";
import { Icon } from "PFComponents/icon";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { exportAccountConfig } from "PFCore/services/accounts/account";
import { ApiRoute, UiRoute } from "PFCore/utilities/routes";
import SearchIcon from "PFIcons/search.svg";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import { PaginatedCollection } from "PFTypes";
import { Account } from "PFTypes/account";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import css from "./accounts_collection_page.module.scss";
import { AccountConfig, ConfigModal } from "./components/config_modal";

type AccountsCollectionPageProps = {
  accounts: PaginatedCollection<Account[]>;
  fetch: (page?: number, perPage?: number, term?: string) => void;
};

export const AccountsCollectionPage = ({ accounts, fetch }: AccountsCollectionPageProps) => {
  const { t } = useTranslation("saasAdmin");
  const { t: tAdmin } = useTranslation("admin", { keyPrefix: "accessRequests" });

  const history = useHistory();
  const {
    store: { currentAccount },
    dispatch
  } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();
  const { getAccessToken } = useSession();
  const { entries, meta } = accounts;

  const [config, setConfig] = useState<AccountConfig | null>(null);

  const handleConfigDownload = (id: number) =>
    exportAccountConfig(id)
      .then((response) => {
        setConfig(decamelizeKeys(response) as AccountConfig);
      })
      .catch(() => {
        dispatch({
          type: GROWL_ACTIONS.GROWL_RENDER,
          payload: {
            kind: "error",
            message: t("configDownloadError")
          }
        });
      });

  const actionsRenderer = (data) => {
    const options = [
      {
        id: "customization",
        displayElement: t("list.customization"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/customization`)
      },
      {
        id: "cv_parsing_conf",
        displayElement: t("list.cvParsingConf"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/cv_parsing_conf`)
      },
      {
        id: "cv_templates",
        displayElement: t("list.cvTemplates"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/cv_templates`)
      },
      {
        id: "features",
        displayElement: t("list.features"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/features`)
      },
      {
        id: "imports",
        displayElement: t("list.imports"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/imports`)
      },
      {
        id: "activity_templates",
        displayElement: t("list.activityTemplates"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/activity_templates`)
      },
      {
        id: "filters_and_order",
        displayElement: t("list.filters_and_order"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/filters_and_order`)
      },
      {
        id: "localisation",
        displayElement: t("list.localisation"),
        item: () => history.push(`/saas_admin/accounts/${data.id}/localisation`)
      }
    ];

    return (
      <div className={css.actions}>
        <form method={"get"} action={ApiRoute("/api/oauth/authorize")} data-qa-id="SignInAsAction.form">
          <input type="hidden" name="redirect_uri" value={UiRoute("/", data.full_domain)} />
          <input type="hidden" name="grant_type" value={"sign_in_as"} />
          <input type="hidden" name="response_type" value={"code"} />
          <input type="hidden" name="target_profile_id" value={currentProfile.id} />
          <input type="hidden" name="client_id" value={PF.config.client_id} />
          <input type="hidden" name="access_token" value={getAccessToken()} />
          <input type="hidden" name="account" value={data.full_domain} />

          <Button
            small
            text={t("table.switch")}
            kind="secondary"
            data-qa-id="SignInAsAction.button"
            disabled={data?.id === currentAccount?.id}
          />
        </form>

        <ButtonLink kind="secondary" small href={`/saas_admin/accounts/${data.id}/edit`}>
          {t("table.edit")}
        </ButtonLink>
        <Button kind="secondary" small onClick={() => handleConfigDownload(data.id)}>
          {t("table.config")}
        </Button>
        <DropdownButton small buttonKind="secondary" options={options} handleChange={(fn) => fn && fn()}>
          <Icon name="caret-down" size="xs" />
        </DropdownButton>
      </div>
    );
  };

  const realRenderer = ({ real }) => (
    <div className={classNames(css.box, { [css.real]: real })}>{real ? "YES" : "NO"}</div>
  );

  const handleSearch = (term) => fetch(meta.page, 50, term);

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <h1>Accounts</h1>
        <div className={css.headerActions}>
          <ButtonLink href="/saas_admin/accounts/new" kind="secondary">
            New Account
          </ButtonLink>
          <InputFieldSet
            label={t("searchAccount")}
            inputType="text"
            icon={<SearchIcon width={20} height={20} />}
            onChange={handleSearch}
          />
        </div>
      </div>
      <PageBox>
        {meta.total > 0 ? (
          <DataTable
            style={{ overflow: "visible" }}
            columns={[
              {
                headerName: t("list.name"),
                cellKey: "name"
              },
              {
                headerName: t("list.domain"),
                cellKey: "full_domain"
              },
              {
                headerName: t("list.real"),
                renderCell: (data) => realRenderer(data)
              },
              {
                headerName: t("table.action"),
                renderCell: (data) => actionsRenderer(data),
                narrow: true
              }
            ]}
            data={entries}
          />
        ) : (
          <div className={css.notFound}>{tAdmin("accessRequestNotFound")}</div>
        )}
      </PageBox>
      <div className={css.pagination}>
        <SimplePagination currentPage={meta?.page} totalPages={meta?.totalPages} handleClick={fetch} />
      </div>

      {!!config && (
        <ConfigModal
          config={config}
          onClose={() => setConfig(null)}
          onChange={(id) => {
            handleConfigDownload(id);
            fetch(meta.page);
          }}
        />
      )}
    </div>
  );
};
